'use client';
import { fleetApi } from '../api/axiosClient';
import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { assertApiSuccess } from '../utils/response';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

export const revalidateServiceTypeList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/service-type/search');
};

const fetcher = async ([url, payloadStr]) => {
  const payload = JSON.parse(payloadStr);
  const resp = await fleetApi.post(url, payload);
  return resp.data;
};

export const SERVICE_TYPE_SEARCH_FIELDS = {
  service_type_name: { name: 'name', label: 'Service Type Name' },
  name: { name: 'name', label: 'Service Type Name' },
  service_length_unit: { name: 'service_length_unit', label: 'Schedule By' },
  interval_distance: { name: 'interval_distance', label: 'Distance (Km)' },
  interval_time: { name: 'interval_time', label: 'Days' },
  service_cost: { name: 'service_cost', label: 'Service Cost' }
};

const EMPTY_SERVICE_TYPE_COLUMN = { logic_operator: 'like', name: 'name', operator: 'AND', value: '' };

export const buildServiceTypeSearchPayload = ({
  start = 0,
  length = 25,
  searchKey = 'all',
  searchValue = '',
  selectedCompanyIds = ''
} = {}) => {
  const fb = new FilterBuilder().setPaging(start, length).addJoin('company', ['name']).setOrder(['name'], true);

  const companyIdsStr = selectedCompanyIds ? String(selectedCompanyIds) : '';
  if (companyIdsStr && companyIdsStr !== '') {
    if (companyIdsStr.includes(',')) {
      fb.in('company_id', companyIdsStr);
    } else {
      fb.equal('company_id', companyIdsStr);
    }
  } else {
    fb.filterByCompany();
  }

  const query = String(searchValue ?? '').trim();
  if (query) {
    if (!searchKey || searchKey === 'all') {
      const seen = new Set();
      Object.values(SERVICE_TYPE_SEARCH_FIELDS).forEach((f) => {
        const key = `${f.name}-${f.table_name ?? ''}`;
        if (!seen.has(key)) {
          seen.add(key);
          fb.like(f.name, query, 'OR', f.table_name ?? null);
        }
      });
    } else {
      const f = SERVICE_TYPE_SEARCH_FIELDS[searchKey] || { name: searchKey };
      fb.like(f.name, query, 'AND', f.table_name ?? null);
    }
  }

  const built = fb.build();
  if (!built.columns || built.columns.length === 0) {
    built.columns = [{ ...EMPTY_SERVICE_TYPE_COLUMN }];
  }
  return built;
};

export const fetchAllServiceType = async ({ searchKey = 'all', searchValue = '', selectedCompanyIds = '', total = 0 } = {}) => {
  const payload = buildServiceTypeSearchPayload({
    start: 0,
    length: total > 0 ? total : 9999,
    searchKey,
    searchValue,
    selectedCompanyIds
  });
  const resp = await fleetApi.post('/service-type/search' + getTzParam(), payload);
  const data = resp?.data;
  const candidate = data?.data ?? data;
  return Array.isArray(candidate) ? candidate : [];
};

export const useServiceTypeList = (page = 0, rowsPerPage = 25, searchKey = 'all', searchValue = '', selectedCompanyIds = '') => {
  const start = page * rowsPerPage;

  const payload = useMemo(
    () => buildServiceTypeSearchPayload({ start, length: rowsPerPage, searchKey, searchValue, selectedCompanyIds }),
    [start, rowsPerPage, searchKey, searchValue, selectedCompanyIds]
  );

  const key = useMemo(() => ['/service-type/search', JSON.stringify(payload)], [payload]);

  const {
    data: raw,
    error,
    isLoading
  } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  let rows = [];
  let total = 0;

  if (!raw) {
    rows = [];
    total = 0;
  } else {
    const candidate = raw?.data ?? raw;
    const nested = candidate?.data ?? candidate;
    if (Array.isArray(nested)) {
      rows = nested;
      total =
        Number(candidate?.totalRecord ?? candidate?.totalRecords ?? raw?.totalRecord ?? raw?.totalRecords ?? nested.length) ||
        nested.length;
    } else if (Array.isArray(raw)) {
      rows = raw;
      total = raw.length;
    } else {
      const maybeArray = raw?.data ?? null;
      if (Array.isArray(maybeArray)) {
        rows = maybeArray;
        total = Number(raw?.totalRecord ?? raw?.totalRecords ?? maybeArray.length) || maybeArray.length;
      } else {
        rows = [];
        total = 0;
      }
    }
  }

  return {
    data: rows,
    totalRecord: total,
    isLoading: isLoading || (!raw && !error),
    isError: error
  };
};

export const getServiceTypeById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], true).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const resp = await fleetApi.post(`/service-type/search${getTzParam()}`, payload);
    const dataArr = resp?.data?.data ?? resp?.data ?? (Array.isArray(resp) ? resp : null);
    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (error) {
    console.error('Failed to fetch service type by ID:', error);
    throw error;
  }
};

export const createServiceType = async (formData = {}) => {
  try {
    const body = { ...formData };
    const resp = await fleetApi.post(`/service-type/new${getTzParam()}`, body);
    assertApiSuccess(resp, 'Create service type failed');
    revalidateServiceTypeList();
    return resp.data;
  } catch (error) {
    console.error('Error creating service type:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateServiceType = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = { ...formData };
    const resp = await fleetApi.put(`/service-type/edit/${id}${getTzParam()}`, body);
    assertApiSuccess(resp, 'Update service type failed');
    revalidateServiceTypeList();
    return resp.data;
  } catch (error) {
    console.error('Error updating service type:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteServiceType = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.delete(`/service-type/delete/${id}`);
    revalidateServiceTypeList();
    return resp.data;
  } catch (error) {
    console.error('Error deleting service type:', error?.response?.data ?? error);
    throw error;
  }
};
export const deleteVehicleServiceType = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.delete(`/vehicle-service-type/delete/${id}`);
    revalidateServiceTypeList();
    return resp.data;
  } catch (error) {
    console.error('Error deleting service type:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateVhicleServiceType = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  const status = formData.status || '';
  const vehicle = formData.vehicle || 0;
  const current_odometer = formData.current_odometer || 0;
  const actual_odometer = formData.actual_odometer || 0;
  const workshop = formData.workshop || 0;
  const vm_number = formData.invoice_number || '';
  const date_completed = formData.date_completed?.split('T')[0] || '';
  const date_completed_actual = formData.date_completed_actual?.split('T')[0] || '1900-01-01';
  const urlPath = `/vehicle-service-type/edit/${id}/${status}/${vehicle}/${current_odometer}/${actual_odometer}/${workshop}/${vm_number}/${date_completed}/${date_completed_actual}`;

  try {
    const body = {
      service_type: formData.service_type,
      invoice_number: formData.invoice_number,
      price: Number(formData.price || 0),
      remarks: formData.remarks || '',
      next_service_odo: Number(formData.next_service_odo || 0),
      next_service_date: formData.next_service_date || null
    };

    const resp = await fleetApi.put(urlPath, body);
    assertApiSuccess(resp, 'Update vehicle service type failed');
    revalidateServiceTypeList();
    return resp.data;
  } catch (error) {
    console.error('Error updating vehicle service type via URL:', error?.response?.data ?? error);
    throw error;
  }
};

export const createVehicleServiceType = async (formData = {}) => {
  try {
    const body = { ...formData };
    const resp = await fleetApi.post(`/vehicle-service-type/new`, body);
    assertApiSuccess(resp, 'Create vehicle service type failed');
    revalidateServiceTypeList();
    return resp.data;
  } catch (error) {
    console.error('Error creating service type:', error?.response?.data ?? error);
    throw error;
  }
};
